/**
 * HTTP + WebSocket contract between the client/admin apps and the API service.
 * All REST routes live under /v1 and exchange JSON. Authenticated routes take
 * `Authorization: Bearer <accessToken>`. Errors are `{ error: string, code?: string }`
 * with a non-2xx status.
 */
import type { Cosmetics, Currency, Loadout, ModeId, Rarity, WeaponTuning } from '../game/types';
import type { HighlightKind } from '../net/replay';

// Common ----------------------------------------------------------------------------------

export interface ApiError {
  error: string;
  code?:
    | 'unauthorized'
    | 'forbidden'
    | 'not_found'
    | 'invalid'
    | 'insufficient_funds'
    | 'locked'
    | 'already_owned'
    | 'rate_limited'
    | 'banned'
    | 'restricted'
    | 'conflict'
    | 'unavailable'
    /** HTTP 426: this app version is older than `client.versions` allows. */
    | 'update_required'
    /** HTTP 403: staff routes need a session confirmed with an authenticator code (POST /v1/auth/two-factor/*). */
    | 'two_factor_required';
}

export interface Wallet {
  coins: number;
  gems: number;
}

export type ItemType = 'weapon' | 'gadget' | 'skin' | 'armor' | 'camo' | 'tracer' | 'scope' | 'deathfx' | 'banner' | 'title' | 'pet' | 'petstyle';

export interface InventoryItem {
  type: ItemType;
  id: string;
  acquiredAt: string;
}

export interface RewardBundle {
  coins?: number;
  gems?: number;
  xp?: number;
  bpXp?: number;
  items?: { type: ItemType; id: string }[];
}

// Auth -----------------------------------------------------------------------------------------

/** POST /v1/auth/guest */
export interface GuestAuthRequest {
  deviceId: string;
  /** Used only to decide restricted (under-13) mode; never stored beyond the flag + year. */
  birthYear: number;
  locale?: string;
  platform?: 'web' | 'android' | 'ios' | 'desktop';
  /** First-touch acquisition (utm_source / ref / referring site), kept for analytics only. */
  attribution?: Attribution;
  appVersion?: string;
  /** Touch screen (tells iPads apart from Macs, whose Safari reports a desktop). */
  touch?: boolean;
}

export interface Attribution {
  source?: string;
  campaign?: string;
  referrer?: string;
}

/** POST /v1/auth/register (authenticated guest links an email) */
export interface RegisterRequest {
  email: string;
  password: string;
}

/** POST /v1/auth/login */
/** POST /v1/auth/forgot-password: always { ok: true } (it never tells whether the email has an account). */
export interface ForgotPasswordRequest {
  email: string;
}

/** POST /v1/auth/reset-password: the token from the emailed link (#reset=...) and the new password. */
export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface ResetPasswordResponse {
  ok: true;
  /** The account's email, so the app can sign in with the new password. */
  email: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  deviceId: string;
}

/**
 * POST /v1/auth/delete-account: the website's deletion page (https://<domain>/delete-account), for accounts
 * with a linked email. Without `confirm` it only checks the email and password and names the account.
 */
export interface DeleteAccountRequest {
  email: string;
  password: string;
  confirm?: boolean;
}

export interface DeleteAccountResponse {
  /** The account's call sign. */
  name: string;
  deleted: boolean;
}

/** POST /v1/auth/refresh */
export interface RefreshRequest {
  refreshToken: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  /** Seconds until the access token expires. */
  expiresIn: number;
  me: MeResponse;
}

// Profile ----------------------------------------------------------------------------------------

export interface ProfileStats {
  kills: number;
  deaths: number;
  assists: number;
  wins: number;
  losses: number;
  matches: number;
  headshots: number;
  voxelsDestroyed: number;
  voxelsBuilt: number;
  collapseKills: number;
  playtimeSec: number;
  bestStreak: number;
  rating: number;
  /** Kills per weapon id (drives weapon mastery). */
  weaponKills: Record<string, number>;
}

export interface Progression {
  level: number;
  xp: number;
  /** XP required to go from the current level to the next. */
  xpToNext: number;
  /** XP accumulated inside the current level. */
  xpInLevel: number;
  prestige: number;
}

export interface CustomSkin {
  slot: number;
  name: string;
  /** Base64 skin bytes (SKIN_BYTES long). */
  data: string;
  updatedAt: string;
}

export interface MeResponse {
  id: string;
  name: string;
  /** Friend code, e.g. "VM-7Q2K9". */
  code: string;
  email: string | null;
  isGuest: boolean;
  /** Under-13: quick chat only, generated names, no web purchases, no personalized ads. */
  restricted: boolean;
  role: 'player' | 'moderator' | 'admin';
  /** Staff only (null for players): two-step sign-in, required for the admin console. `verified`: this session passed it. */
  twoFactor?: { enabled: boolean; verified: boolean } | null;
  createdAt: string;
  wallet: Wallet;
  progression: Progression;
  stats: ProfileStats;
  inventory: InventoryItem[];
  loadout: Loadout;
  cosmetics: Cosmetics;
  customSkins: CustomSkin[];
  clan: ClanSummary | null;
  /** True once the first free rename has been used. */
  renamed: boolean;
  unreadInbox: number;
  dailyLogin: { day: number; claimedToday: boolean; nextReward: RewardBundle };
  mutedUntil: string | null;
  tutorialDone: boolean;
}

/** PATCH /v1/me */
export interface UpdateMeRequest {
  name?: string;
  tutorialDone?: boolean;
}

/** PUT /v1/me/loadout */
export type UpdateLoadoutRequest = Loadout;

/** PUT /v1/me/cosmetics */
export type UpdateCosmeticsRequest = Cosmetics;

/** PUT /v1/me/skins/:slot */
export interface SaveSkinRequest {
  name: string;
  data: string;
}

export interface PublicProfile {
  id: string;
  name: string;
  level: number;
  clan: ClanSummary | null;
  stats: Pick<ProfileStats, 'kills' | 'deaths' | 'wins' | 'matches' | 'rating' | 'voxelsDestroyed'>;
  cosmetics: Cosmetics;
  isFriend: boolean;
  online: boolean;
}

// Catalog / shop ----------------------------------------------------------------------------------

export interface CatalogEntry {
  type: ItemType;
  id: string;
  name: string;
  rarity: Rarity;
  price: number;
  currency: Currency;
  unlockLevel: number;
  /** Not directly purchasable (battle pass, achievements, events). */
  source?: 'shop' | 'battlepass' | 'achievement' | 'event' | 'starter';
  /** Taken out of play for now (weapon kill switch): can't be bought, and loadouts spawn with another weapon. */
  disabled?: boolean;
}

export interface GemPack {
  id: string;
  name: string;
  gems: number;
  bonus: number;
  /** Price in USD cents (platform stores localize). */
  priceCents: number;
  /** Store SKUs per platform. */
  skus: { web?: string; android?: string; ios?: string };
  badge?: string;
}

export interface StoreOffer {
  id: string;
  name: string;
  items: { type: ItemType; id: string }[];
  price: number;
  currency: Currency;
  /** ISO date when the offer rotates out. */
  endsAt: string;
  discountPct?: number;
  featured?: boolean;
}

export interface LevelInfo {
  level: number;
  xpToNext: number;
  rewards: RewardBundle;
}

/** GET /v1/catalog (public, cacheable by `version`). */
export interface CatalogResponse {
  version: string;
  items: CatalogEntry[];
  gemPacks: GemPack[];
  offers: StoreOffer[];
  levels: LevelInfo[];
  season: SeasonInfo;
  motd: string | null;
  /** Mode ids currently enabled in the play menu. */
  modes: ModeId[];
  events: LiveEvent[];
  /** The 7-day login cycle (live-configurable). */
  dailyLogin?: RewardBundle[];
  /** Maps not offered right now, per mode (off in the rotation or disabled everywhere; events can bring them back). */
  mapsOff?: Partial<Record<ModeId, string[]>>;
  /** Live weapon balance: offline practice applies it too, and the armory shows the tuned stats. */
  weapons?: WeaponBalanceConfig;
  /** Oldest app versions allowed; older apps show an update screen. */
  clientVersions?: ClientVersionsConfig;
}

export interface LiveEvent {
  id: string;
  name: string;
  description: string;
  startsAt: string;
  endsAt: string;
  /** e.g. { xpMultiplier: 2 }, { mode: 'zombies' } or { map: 'frostbite', mapWeight: 3 } (see ConfigValues.events). */
  effects: Record<string, number | string>;
}

/** GET /v1/dev/status — whether the Test Lab is available on this API (development builds only). */
export interface DevStatusResponse {
  enabled: boolean;
}

/** POST /v1/dev/lab — Test Lab actions for local testing. Every field is optional. */
export interface DevLabRequest {
  /** Own every purchasable item (weapons, scopes, tracers, camos, looks, ...). */
  unlockAll?: boolean;
  /** Set the account level (XP follows). */
  level?: number;
  /** Add currency. */
  addCoins?: number;
  addGems?: number;
  /** Credit this many takedowns to EVERY weapon (mastery tiers), taking the larger of current and this. */
  masteryKills?: number;
}

/** POST /v1/shop/buy */
export interface BuyRequest {
  type: ItemType | 'offer';
  id: string;
}

export interface BuyResponse {
  wallet: Wallet;
  inventory: InventoryItem[];
}

/** POST /v1/iap/checkout */
export interface CheckoutRequest {
  packId: string;
  platform: 'web' | 'android' | 'ios' | 'desktop';
  returnUrl?: string;
}

export type CheckoutResponse =
  | { provider: 'stripe'; url: string; sessionId: string }
  | { provider: 'native'; sku: string }
  | { provider: 'dev'; wallet: Wallet };

/** POST /v1/iap/verify (native store receipts) */
export interface VerifyPurchaseRequest {
  platform: 'android' | 'ios';
  packId: string;
  receipt: string;
  transactionId?: string;
}

/** POST /v1/rewards/daily */
export interface DailyLoginResponse {
  day: number;
  reward: RewardBundle;
  wallet: Wallet;
}

/** POST /v1/rewards/bootcamp — one-time reward for finishing Boot Camp (409 once claimed). */
export interface BootCampRewardResponse {
  reward: RewardBundle;
  wallet: Wallet;
}

/** POST /v1/rewards/ad {placement} — rewarded ads (not available to restricted accounts). */
export interface AdRewardResponse {
  reward: RewardBundle;
  wallet: Wallet;
  remainingToday: number;
}

// Battle pass / quests / achievements ------------------------------------------------------------------

export interface SeasonInfo {
  id: string;
  name: string;
  startsAt: string;
  endsAt: string;
  tiers: number;
  xpPerTier: number;
  premiumPriceGems: number;
  rewards: { tier: number; free: RewardBundle | null; premium: RewardBundle | null }[];
}

export interface BattlePassState {
  season: SeasonInfo;
  xp: number;
  tier: number;
  premium: boolean;
  claimed: { tier: number; track: 'free' | 'premium' }[];
}

/** POST /v1/battlepass/claim */
export interface ClaimTierRequest {
  tier: number;
  track: 'free' | 'premium';
}

export interface Quest {
  id: string;
  kind: 'daily' | 'weekly';
  title: string;
  description: string;
  /** Stat the quest tracks, e.g. 'kills', 'kills:sniper', 'wins', 'voxels', 'headshots', 'mode:dom'. */
  metric: string;
  goal: number;
  progress: number;
  reward: RewardBundle;
  completed: boolean;
  claimed: boolean;
  expiresAt: string;
}

export interface QuestsResponse {
  daily: Quest[];
  weekly: Quest[];
  rerollsLeft: number;
}

export interface Achievement {
  id: string;
  title: string;
  description: string;
  metric: string;
  goal: number;
  progress: number;
  reward: RewardBundle;
  unlocked: boolean;
  claimed: boolean;
  icon: string;
}

// Match rewards ----------------------------------------------------------------------------------------

export interface MatchRewards {
  matchId: string;
  mode: ModeId;
  won: boolean;
  placement: number;
  xp: number;
  coins: number;
  bpXp: number;
  breakdown: { label: string; xp: number; coins: number }[];
  levelBefore: number;
  levelAfter: number;
  levelRewards: RewardBundle[];
  questsCompleted: string[];
  achievementsUnlocked: string[];
  wallet: Wallet;
  progression: Progression;
}

// Leaderboards -------------------------------------------------------------------------------------------

export type LeaderboardId = 'level' | 'kills' | 'wins' | 'rating' | 'voxels' | 'clans';

export interface LeaderboardEntry {
  rank: number;
  id: string;
  name: string;
  clanTag?: string;
  value: number;
}

/** GET /v1/leaderboards/:board?period=week|all&limit=50 */
export interface LeaderboardResponse {
  board: LeaderboardId;
  period: 'week' | 'all';
  entries: LeaderboardEntry[];
  me: LeaderboardEntry | null;
}

// Friends / reports --------------------------------------------------------------------------------------

export type Presence = 'offline' | 'lobby' | 'in_match' | 'away';

export interface Friend {
  id: string;
  name: string;
  level: number;
  clanTag?: string;
  presence: Presence;
  /** Mode being played when in a match. */
  activity?: string;
  since: string;
}

/** GET /v1/friends */
export interface FriendsResponse {
  friends: Friend[];
  incoming: Friend[];
  outgoing: Friend[];
  blocked: { id: string; name: string }[];
}

/** POST /v1/friends/request — by friend code or exact name. */
export interface FriendRequest {
  code?: string;
  name?: string;
}

/** POST /v1/reports */
export interface ReportRequest {
  targetId: string;
  reason: 'cheating' | 'harassment' | 'inappropriate_name' | 'inappropriate_skin' | 'spam' | 'other';
  details?: string;
  matchId?: string;
}

// Clans -------------------------------------------------------------------------------------------------

export interface ClanEmblem {
  shape: number;
  color: number;
  icon: number;
  iconColor: number;
}

export interface ClanSummary {
  id: string;
  name: string;
  tag: string;
  emblem: ClanEmblem;
  level: number;
  members: number;
  maxMembers: number;
  joinPolicy: 'open' | 'request' | 'closed';
}

export interface ClanMember {
  id: string;
  name: string;
  level: number;
  role: 'leader' | 'officer' | 'member';
  contribution: number;
  presence: Presence;
  joinedAt: string;
}

export interface ClanDetail extends ClanSummary {
  description: string;
  xp: number;
  xpToNext: number;
  createdAt: string;
  roster: ClanMember[];
  requests: { id: string; name: string; level: number }[];
  weeklyRank: number | null;
}

/** POST /v1/clans (costs coins; see CLAN_CREATE_COST) */
export interface CreateClanRequest {
  name: string;
  tag: string;
  description: string;
  emblem: ClanEmblem;
  joinPolicy: ClanSummary['joinPolicy'];
}

export const CLAN_CREATE_COST = 5000;
export const CLAN_MAX_MEMBERS = 30;

// Inbox ---------------------------------------------------------------------------------------------------

export interface InboxMessage {
  id: string;
  title: string;
  body: string;
  rewards: RewardBundle | null;
  claimed: boolean;
  read: boolean;
  createdAt: string;
  expiresAt: string | null;
}

// Matchmaking / rooms / party -------------------------------------------------------------------------------

export interface MatchAssignment {
  matchId: string;
  serverUrl: string;
  ticket: string;
  mode: ModeId;
  mapId: string;
  roomCode?: string;
}

/** POST /v1/play/quick */
export interface QuickPlayRequest {
  mode: ModeId;
  region?: string;
  mapId?: string;
}

export type QuickPlayResponse = { state: 'matched'; match: MatchAssignment } | { state: 'queued'; queueId: string; estimatedWaitSec: number };

/** POST /v1/rooms */
export interface CreateRoomRequest {
  mode: ModeId;
  mapId: string;
  maxPlayers: number;
  bots: number;
  botSkill: number;
  timeLimit: number;
  scoreLimit: number;
  password?: string;
  public: boolean;
}

/** POST /v1/rooms/join */
export interface JoinRoomRequest {
  code: string;
  password?: string;
}

export interface RoomInfo {
  code: string;
  mode: ModeId;
  mapId: string;
  players: number;
  maxPlayers: number;
  hasPassword: boolean;
  host: string;
}

export interface Party {
  id: string;
  leaderId: string;
  members: { id: string; name: string; level: number; ready: boolean }[];
  mode: ModeId | null;
}

// Social WebSocket: GET /v1/ws?token=<accessToken> ------------------------------------------------------------

export type ChatChannel = 'global' | 'clan' | 'party' | `dm:${string}`;

export type SocialClientMsg =
  | { t: 'chat'; channel: ChatChannel; text: string }
  | { t: 'presence'; status: Presence; activity?: string }
  | { t: 'ping' };

export interface ChatMessage {
  id: string;
  channel: ChatChannel;
  from: { id: string; name: string; clanTag?: string; role?: MeResponse['role'] };
  text: string;
  ts: number;
}

export type SocialServerMsg =
  | { t: 'hello'; online: number }
  | { t: 'chat'; msg: ChatMessage }
  | { t: 'chat_history'; channel: ChatChannel; msgs: ChatMessage[] }
  | { t: 'presence'; userId: string; presence: Presence; activity?: string }
  | { t: 'friend_request'; from: Friend }
  | { t: 'friend_update' }
  | { t: 'party'; party: Party | null }
  | { t: 'party_invite'; from: { id: string; name: string }; partyId: string }
  | { t: 'match_found'; match: MatchAssignment }
  | { t: 'queue'; state: 'searching' | 'cancelled'; waited: number }
  | { t: 'rewards'; rewards: MatchRewards }
  | { t: 'wallet'; wallet: Wallet }
  | { t: 'inbox'; unread: number }
  | { t: 'clan_update' }
  | { t: 'notice'; kind: 'info' | 'warning' | 'error'; text: string }
  | { t: 'pong' }
  | { t: 'kicked'; reason: string };

// Admin (/v1/admin/*, role admin or moderator) -------------------------------------------------------------------

/** Off-site copies of the database backups (apps/api/src/services/offsite.ts). */
export interface OffsiteBackupStatus {
  /** 'ok': a copy from the last day is off the server; 'stale': none that recent; 'error': the last run failed or the service stopped; 'off': not set up. */
  state: 'ok' | 'stale' | 'error' | 'off';
  problem: string | null;
  lastRunAt: string | null;
  newest: { name: string; at: string } | null;
  copies: number;
}

export interface AdminStats {
  /** Off-site backup copies (the dashboard warns when they stop). */
  offsiteBackup: OffsiteBackupStatus;
  online: number;
  inMatch: number;
  matches: number;
  servers: number;
  dau: number;
  mau: number;
  signups7d: number;
  revenue7dCents: number;
  reportsOpen: number;
  /** Support conversations waiting in the Open tab. */
  supportOpen: number;
}

export interface AdminUserRow {
  id: string;
  name: string;
  email: string | null;
  level: number;
  createdAt: string;
  lastSeenAt: string | null;
  bannedUntil: string | null;
  mutedUntil: string | null;
  restricted: boolean;
  role: MeResponse['role'];
  wallet: Wallet;
}

export interface AdminUserDetail extends AdminUserRow {
  stats: ProfileStats;
  inventory: InventoryItem[];
  reports: AdminReport[];
  purchases: AdminPurchase[];
  ledger: { ts: string; coins: number; gems: number; reason: string }[];
  banReason: string | null;
}

export interface AdminReport {
  id: string;
  reporterId: string;
  reporterName: string;
  targetId: string;
  targetName: string;
  reason: ReportRequest['reason'];
  details: string | null;
  matchId: string | null;
  status: 'open' | 'resolved' | 'dismissed';
  createdAt: string;
  resolution: string | null;
}

export interface AdminPurchase {
  id: string;
  userId: string;
  provider: 'stripe' | 'apple' | 'google' | 'dev';
  packId: string;
  amountCents: number;
  status: 'pending' | 'completed' | 'refunded' | 'failed';
  createdAt: string;
}

export interface AdminServer {
  id: string;
  url: string;
  region: string;
  capacity: number;
  load: number;
  matches: number;
  players: number;
  lastHeartbeat: string;
}

export interface AdminMatch {
  id: string;
  serverId: string;
  mode: ModeId;
  mapId: string;
  humans: number;
  bots: number;
  private: boolean;
  roomCode?: string;
}

export interface AdminMatchPlayer {
  /** In-match player id. */
  id: number;
  /** Account id (absent for bots and dev logins). */
  userId?: string;
  name: string;
  team: number;
  isBot: boolean;
  kills: number;
  deaths: number;
  score: number;
  ping: number;
  /** Seconds since joining. */
  joinedSec: number;
}

/** GET /v1/admin/matches/:id (live roster, fetched from the game server hosting the match). */
export interface AdminMatchDetail extends AdminMatch {
  phase: 'playing' | 'ended';
  /** Seconds since the match started. */
  elapsedSec: number;
  /** Seconds left on the clock (timed modes). */
  timeLeft?: number;
  maxPlayers: number;
  players: AdminMatchPlayer[];
  /** Weapons with live stat overrides / disabled in this match (fixed when the match was created). */
  tunedWeapons: number;
  disabledWeapons: number;
}

/** POST /v1/admin/matches/:id/end: ends the match now; results and rewards are handed out as usual. */
export interface AdminEndMatchRequest {
  /** Shown to the players in the match. */
  reason?: string;
}

/** POST /v1/admin/matches/:id/kick: removes a player; they can't rejoin that match. */
export interface AdminKickRequest {
  userId: string;
  /** Shown to the kicked player. */
  reason?: string;
}

/** Tunable live-ops config keys (GET/PUT /v1/admin/config/:key). */
export type ConfigKey =
  | 'motd'
  | 'store.offers'
  | 'prices'
  | 'events'
  | 'chat.blocklist'
  | 'modes.enabled'
  | 'xp.multiplier'
  | 'coins.multiplier'
  | 'maintenance'
  | 'seasons'
  | 'economy.dailyLogin'
  | 'economy.ads'
  | 'economy.quests'
  | 'maps.rotation'
  | 'modes.tuning'
  | 'weapons.balance'
  | 'client.versions'
  | 'highlights.capture';

/** Game servers record highlight replays of public matches for marketing clips. */
export interface HighlightCaptureConfig {
  enabled: boolean;
  /** How good a moment must be (triple takedown ≈ 70, building collapse onto a player ≈ 60). */
  minScore: number;
  /** Most highlights kept from one match. */
  perMatch: number;
}

/** A recorded highlight (GET /v1/admin/highlights). */
export interface AdminHighlight {
  id: string;
  matchId: string;
  serverId: string;
  mode: ModeId;
  mapId: string;
  kind: HighlightKind;
  label: string;
  score: number;
  kills: number;
  durationSec: number;
  /** The featured player (staff only; clips never show names). */
  subjectName: string;
  subjectUserId: string | null;
  subjectIsBot: boolean;
  starred: boolean;
  /** Shown on the website (and never cleaned up). */
  published: boolean;
  /** Public page once shared by the featured player or published (https://<site>/r/<slug>). */
  shareUrl: string | null;
  /** Visits of the public page. */
  views: number;
  /** A still for link previews and the website exists. */
  hasPoster: boolean;
  bytes: number;
  createdAt: string;
}

export interface AdminHighlightsResponse {
  highlights: AdminHighlight[];
  total: number;
}

/**
 * A highlight anyone can watch (GET /v1/public/highlights/:slug). Never names players: the replay
 * itself is anonymized (human players become "Marine N").
 */
export interface PublicHighlight {
  slug: string;
  label: string;
  kind: HighlightKind;
  mode: ModeId;
  mapId: string;
  /** Display names ("Team Battle", "Brick Plaza"). */
  modeName: string;
  mapName: string;
  durationSec: number;
  createdAt: string;
  /** Public page (https://<site>/r/<slug>). */
  url: string;
  /** Still image for link previews and galleries (null until one was made). */
  posterUrl: string | null;
}

/** GET /v1/public/featured: highlights staff published for the website, newest first. */
export interface PublicFeaturedResponse {
  highlights: PublicHighlight[];
}

/** GET /v1/public/stats: numbers for the website (refreshed every minute). */
export interface PublicStats {
  /** Blocks destroyed by players in finished matches, all time. */
  blocksDestroyed: number;
  matchesPlayed: number;
  /** Players connected right now. */
  online: number;
}

/** A highlight of a match the player was in (Play of the Game, their own highlights). */
export interface PlayerHighlight {
  id: string;
  matchId: string;
  label: string;
  kind: HighlightKind;
  mode: ModeId;
  mapId: string;
  score: number;
  durationSec: number;
  createdAt: string;
  /** In-match name of the featured player ('' when none). */
  subjectName: string;
  /** The player is the featured player (only they can share it). */
  mine: boolean;
  /** Public page once shared. */
  shareUrl: string | null;
  hasPoster: boolean;
}

export interface PlayerHighlightsResponse {
  highlights: PlayerHighlight[];
}

/** POST /internal/highlights: x-highlight header (base64url JSON) + gzip replay body. */
export interface HighlightUploadMeta {
  matchId: string;
  serverId: string;
  mode: ModeId;
  mapId: string;
  kind: HighlightKind;
  label: string;
  score: number;
  kills: number;
  durationSec: number;
  subjectName: string;
  subjectUserId: string | null;
  subjectIsBot: boolean;
}

/** Which maps quick play picks, per mode. Events can feature a map (`map` effect) for a while. */
export interface MapRotationConfig {
  /** Maps taken out of every mode: quick play, private rooms and practice. */
  disabled: string[];
  /** Mode → map id → relative pick weight for quick play (missing = 1, 0 = off in that mode). */
  weights: Partial<Record<ModeId, Record<string, number>>>;
}

/** Match settings for quick play in one mode; unset fields keep the mode's defaults. */
export interface ModeTuning {
  maxPlayers?: number;
  /** Bots fill the match up to this many players (0 = no bots). */
  bots?: number;
  /** 0.05 (harmless) .. 1 (sharp). */
  botSkill?: number;
  /** Seconds (timed modes only). */
  timeLimit?: number;
  scoreLimit?: number;
}

/** Live weapon balance: per-weapon stat overrides and the kill switch. New matches pick it up. */
export interface WeaponBalanceConfig {
  /** Weapons taken out of play (not sold, not spawned; loadouts fall back to another weapon of the slot). */
  disabled: string[];
  /** Weapon id → stat key → value (keys from WEAPON_TUNING_FIELDS in @vm/content). */
  overrides: Record<string, WeaponTuning>;
}

export type ClientPlatform = 'web' | 'android' | 'ios' | 'desktop';

/** Forced updates: apps older than the minimum get an update screen and HTTP 426 from the API. */
export interface ClientVersionsConfig {
  /** Oldest allowed app version per platform ("1.4.0"); unset = any version. */
  min: Partial<Record<ClientPlatform, string>>;
  /** Where players get the update (store pages, download page). Web players just reload. */
  urls: Partial<Record<ClientPlatform, string>>;
  /** Shown on the update screen. */
  message: string;
}

/** A rewarded-ad placement (what one ad pays and how many per day). */
export interface AdPlacementConfig {
  id: string;
  name: string;
  reward: RewardBundle;
  dailyLimit: number;
}

export interface EconomyAdsConfig {
  placements: AdPlacementConfig[];
  /** Ads per account per UTC day, all placements together. */
  dailyLimit: number;
  minIntervalSec: number;
}

export interface EconomyQuestsConfig {
  /** Quest templates never handed out. */
  disabled: string[];
  /** Per template: a different goal and/or reward. */
  overrides: Record<string, { goal?: number; reward?: RewardBundle }>;
}

export interface AdminBroadcastRequest {
  title: string;
  body: string;
  rewards?: RewardBundle;
  /** 'all' or a list of user ids. */
  target: 'all' | string[];
  expiresInDays?: number;
}

export interface AdminGrantRequest {
  coins?: number;
  gems?: number;
  items?: { type: ItemType; id: string }[];
  reason: string;
}

export interface TimeSeriesPoint {
  t: string;
  v: number;
}

// Telemetry -----------------------------------------------------------------------------------------------------

/** POST /v1/events (fire-and-forget client analytics) */
export interface TelemetryBatch {
  sessionId: string;
  events: { type: string; ts: number; props?: Record<string, string | number | boolean> }[];
  /** Where this app run happens (sent with every batch; the server keeps one play session per id). */
  context?: TelemetryContext;
}

export interface TelemetryContext {
  appVersion?: string;
  platform?: 'web' | 'android' | 'ios' | 'desktop';
  locale?: string;
  touch?: boolean;
}

// Route payloads (additive; documented in docs/backend.md) ------------------------------------------------------

/** Body of routes that have nothing else to return (logout, DELETE /v1/me, block, report, clan leave...). */
export interface OkResponse {
  ok: true;
}

/** GET /health */
export interface HealthResponse {
  ok: boolean;
  db: boolean;
  uptimeSec: number;
  servers: number;
  online: number;
  /** Off-site database backup copies ('off' when not set up); an uptime monitor can alert on anything but "ok". */
  backups: OffsiteBackupStatus['state'];
}

/**
 * Response of every /v1/me mutation (PATCH /v1/me, PUT /v1/me/loadout, PUT /v1/me/cosmetics,
 * PUT|DELETE /v1/me/skins/:slot): the updated profile.
 */
export type UpdateMeResponse = MeResponse;

/** GET /v1/me/name-suggestions — free generated names. Restricted accounts may only pick names from this list. */
export interface NameSuggestionsResponse {
  names: string[];
}

/** GET /v1/me/matches — recent matches, newest first. */
export interface MatchHistoryEntry {
  matchId: string;
  mode: ModeId;
  mapId: string;
  won: boolean;
  placement: number;
  kills: number;
  deaths: number;
  assists: number;
  score: number;
  xp: number;
  coins: number;
  createdAt: string;
}

export interface MatchHistoryResponse {
  matches: MatchHistoryEntry[];
}

/** POST /v1/iap/verify response. */
export interface VerifyPurchaseResponse {
  purchaseId: string;
  status: 'completed' | 'pending';
  gems: number;
  wallet: Wallet;
}

/** POST /v1/rewards/ad body. Placements: see AD_REWARDS in @vm/content (e.g. 'coins', 'gems', 'bpxp'). */
export interface AdRewardRequest {
  placement: string;
}

/** Response of reward claims (battle pass, quests, achievements, inbox). */
export interface ClaimResponse {
  /** What was granted (already-owned items are replaced by their compensation). */
  reward: RewardBundle;
  wallet: Wallet;
  progression: Progression;
  /** Inventory entries added by this claim. */
  items: InventoryItem[];
}

/** POST /v1/battlepass/claim */
export interface BattlePassClaimResponse extends ClaimResponse {
  state: BattlePassState;
}

/** POST /v1/battlepass/claim-all: every reached, unclaimed tier at once (`reward` is the total). */
export interface BattlePassClaimAllResponse extends BattlePassClaimResponse {
  /** Rewards claimed (tiers × tracks). */
  count: number;
}

/** POST /v1/battlepass/premium */
export interface BattlePassPremiumResponse {
  state: BattlePassState;
  wallet: Wallet;
}

/** POST /v1/quests/:id/claim (reroll returns QuestsResponse). */
export interface QuestClaimResponse extends ClaimResponse {
  quests: QuestsResponse;
}

/** GET /v1/achievements */
export interface AchievementsResponse {
  achievements: Achievement[];
}

/** POST /v1/achievements/:id/claim */
export interface AchievementClaimResponse extends ClaimResponse {
  achievement: Achievement;
}

// Friends routes (request, accept, decline, DELETE /v1/friends/:id) respond with the updated FriendsResponse.

/** GET /v1/clans?query=&limit= */
export interface ClanListResponse {
  clans: ClanSummary[];
}

/** PATCH /v1/clans/:id (leader or officers). Name and tag cannot change. Responds with ClanDetail. */
export interface UpdateClanRequest {
  description?: string;
  emblem?: ClanEmblem;
  joinPolicy?: ClanSummary['joinPolicy'];
}

/** POST /v1/clans/:id/join — 'open' clans join immediately, 'request' clans queue a join request. */
export interface ClanJoinResponse {
  status: 'joined' | 'requested';
  clan: ClanDetail;
}

/** GET /v1/inbox, POST /v1/inbox/:id/read, DELETE /v1/inbox/:id */
export interface InboxResponse {
  messages: InboxMessage[];
  unread: number;
}

/** POST /v1/inbox/:id/claim */
export interface InboxClaimResponse extends ClaimResponse {
  message: InboxMessage;
}

/** GET /v1/rooms/public (POST /v1/rooms and /v1/rooms/join respond with MatchAssignment incl. roomCode). */
export interface PublicRoomsResponse {
  rooms: RoomInfo[];
}

/**
 * Party routes: POST /v1/party {mode?} creates a party (or sets the mode when you lead one) and, like
 * invite/join/kick/ready, responds with the Party. POST /v1/party/leave responds with OkResponse.
 * GET /v1/party responds with PartyResponse. Members also get { t: 'party' } over the social socket.
 */
export interface CreatePartyRequest {
  mode?: ModeId | null;
}

export interface PartyInviteRequest {
  userId: string;
}

export interface PartyJoinRequest {
  partyId: string;
}

export interface PartyKickRequest {
  userId: string;
}

export interface PartyReadyRequest {
  ready: boolean;
}

export interface PartyResponse {
  party: Party | null;
}

// Admin payloads ----------------------------------------------------------------------------------------------

/** GET /v1/admin/users?query=&limit=&offset= (query matches name, email, id or friend code). */
export interface AdminUsersResponse {
  users: AdminUserRow[];
  total: number;
}

/** POST /v1/admin/users/:id/ban (responds with AdminUserDetail, like every user action). */
export interface AdminBanRequest {
  reason: string;
  /** Duration; omitted or 0 = permanent. */
  hours?: number;
}

/** POST /v1/admin/users/:id/mute — hours 0 unmutes. */
export interface AdminMuteRequest {
  hours: number;
  reason?: string;
}

/** POST /v1/admin/users/:id/role (admin only). */
export interface AdminRoleRequest {
  role: MeResponse['role'];
}

/** GET /v1/admin/reports?status=open|resolved|dismissed|all&limit= */
export interface AdminReportsResponse {
  reports: AdminReport[];
  /** Total matching the filters (for paging). */
  total?: number;
  /** Report counts per status (all reasons). */
  counts?: { open: number; resolved: number; dismissed: number };
}

/** POST /v1/admin/reports/:id/resolve (responds with AdminReport). */
export interface AdminResolveReportRequest {
  status: 'resolved' | 'dismissed';
  resolution: string;
  /** Optional punishment applied atomically with the resolution. */
  action?: { type: 'mute' | 'ban'; hours?: number; reason?: string };
}

/** Value shape of every live-ops config key (validated on PUT). */
export interface ConfigValues {
  motd: string | null;
  /** Hand-made offers listed before the daily rotation (expired ones are hidden). */
  'store.offers': StoreOffer[];
  /** Catalog overrides keyed by `${type}:${id}` (e.g. 'armor:gold'); null = no longer sold (source becomes 'event'). */
  prices: Record<string, { price: number; currency: Currency; unlockLevel?: number } | null>;
  /**
   * Live events. Supported effects: xpMultiplier, coinsMultiplier, bpXpMultiplier (numbers),
   * mode (ModeId: multipliers and the featured map only apply to that mode), map (a map id: featured
   * in quick play while the event runs, even if it's off in the rotation) and mapWeight (how much
   * more often the featured map comes up, default 3).
   */
  events: LiveEvent[];
  /** Extra blocked chat/name words, added to the built-in list. */
  'chat.blocklist': string[];
  /** Modes offered in the play menu and accepted by matchmaking. */
  'modes.enabled': ModeId[];
  /** Global reward multipliers (1 = normal, 0..10). */
  'xp.multiplier': number;
  'coins.multiplier': number;
  /** While enabled, non-staff API calls fail with 503 { code: 'unavailable', error: message }. */
  maintenance: { enabled: boolean; message: string };
  /** Battle pass seasons added to (or replacing, by id) the built-in ones. */
  seasons: SeasonInfo[];
  /** The 7-day login cycle rewards. */
  'economy.dailyLogin': RewardBundle[];
  'economy.ads': EconomyAdsConfig;
  'economy.quests': EconomyQuestsConfig;
  'maps.rotation': MapRotationConfig;
  /** Per mode: quick play match settings (player count, bots, bot skill, time and score limits). */
  'modes.tuning': Partial<Record<ModeId, ModeTuning>>;
  'weapons.balance': WeaponBalanceConfig;
  'client.versions': ClientVersionsConfig;
  'highlights.capture': HighlightCaptureConfig;
}

/** GET /v1/admin/config */
export interface AdminConfigResponse {
  values: ConfigValues;
  defaults: ConfigValues;
  updatedAt: Partial<Record<ConfigKey, string>>;
}

/** GET|PUT /v1/admin/config/:key (PUT body: { value }). DELETE resets the key to its default. */
export interface AdminConfigEntry<K extends ConfigKey = ConfigKey> {
  key: K;
  value: ConfigValues[K];
}

export interface AdminServersResponse {
  servers: AdminServer[];
}

export interface AdminMatchesResponse {
  matches: AdminMatch[];
}

export interface AdminBroadcastResponse {
  id: string;
  /** Number of targeted users, or 'all'. */
  recipients: number | 'all';
}

/** GET /v1/admin/analytics?days=30 — one point per UTC day (t = 'YYYY-MM-DD'). */
export interface AdminAnalyticsResponse {
  days: number;
  dau: TimeSeriesPoint[];
  signups: TimeSeriesPoint[];
  matches: TimeSeriesPoint[];
  revenueCents: TimeSeriesPoint[];
}

/** GET /v1/admin/purchases?status=&userId=&limit= */
export interface AdminPurchasesResponse {
  purchases: AdminPurchase[];
  /** Total matching the filters (for paging). */
  total?: number;
  /** Sum of completed, non-dev purchases matching the filters. */
  revenueCents?: number;
}

// Two-step sign-in (staff) ---------------------------------------------------------------------------------------

/** POST /v1/auth/two-factor/setup: starts setup with the account's password; the secret goes into an authenticator app. */
export interface TwoFactorSetupRequest {
  password: string;
}

export interface TwoFactorSetupResponse {
  /** Base32 setup key (for typing in). */
  secret: string;
  /** otpauth:// link (the QR code). */
  otpauthUrl: string;
}

/** POST /v1/auth/two-factor/enable and /verify: a 6-digit authenticator code (or, for verify, a recovery code). */
export interface TwoFactorCodeRequest {
  code: string;
}

/** /enable: one-time recovery codes (shown once) and the session, now confirmed. /verify answers a plain AuthResponse. */
export interface TwoFactorEnableResponse extends AuthResponse {
  recoveryCodes: string[];
}
